/**
 * Land: the deterministic pipeline (fetch → rebase → test → merge → push → verify → prune) as
 * a card, approved with a passkey tap. Progress comes from the run's landing state and the
 * daemon's `landing` notices.
 */

import { Link } from '@tanstack/react-router'
import type { ItemState } from '@viko/transcript-ui'
import {
  Button,
  LandCard,
  type PipelineStep,
  Sheet,
  type TranscriptView,
} from '@viko/transcript-ui'
import { useState } from 'react'
import { stackView } from '../lib/landing-modes.js'
import { elapsed, type LandingStatus } from '../lib/landing-status.js'
import type { Landing, LandingRecipeRun, RunSummary, TaskDetail } from '../lib/types.js'
import { WithinGrantBadge } from './LandGrant.js' // land-grant
import { ConflictWait, GateEvidence, StackBlock, TargetLine } from './LandingModes.js'

const STEPS = [
  ['fetch', 'Fetch'],
  ['rebase', 'Rebase'],
  ['test', 'Test'],
  ['merge', 'Merge'],
  ['push', 'Push'],
  ['verify', 'Verify'],
  ['prune', 'Prune'],
] as const

export function landingProgress(view: TranscriptView): number {
  let n = 0
  const isLanding = (it: { type?: string; code?: string }) =>
    it.type === 'notice' && it.code === 'landing'
  for (const e of view.entries) {
    if (e.kind === 'interstitial') {
      if (isLanding(e.item as never)) n++
    } else {
      for (const it of e.turn.items) if (isLanding(it as never)) n++
    }
  }
  return n
}

export function landSteps(state: RunSummary['landing'], done: number): PipelineStep[] {
  return STEPS.map(([id, label], i) => {
    let s: ItemState = 'pending'
    if (state?.state === 'landed') s = 'ok'
    else if (state?.state === 'running') s = i < done ? 'ok' : i === done ? 'streaming' : 'pending'
    else if (state?.state === 'blocked') s = i < done ? 'ok' : i === done ? 'error' : 'pending'
    return { id, label, state: s }
  })
}

/** Verified endpoint progress remains authoritative when the relay has no private run row. */
export function landingState(
  run: RunSummary | undefined,
  view: TranscriptView,
): RunSummary['landing'] {
  const landed = view.progress.landed
  return landed ? { state: 'landed', ...(landed.sha ? { sha: landed.sha } : {}) } : run?.landing
}

export function LandSheet({
  run,
  view,
  onClose,
  onLand,
}: {
  run: RunSummary
  view: TranscriptView
  onClose: () => void
  onLand: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const landing = landingState(run, view)
  const state = landing?.state ?? 'ready'
  const cardState: ItemState =
    state === 'landed'
      ? 'ok'
      : state === 'running' || busy
        ? 'streaming'
        : state === 'blocked'
          ? 'error'
          : 'awaiting-user'
  const diff = summaryDiff(view)
  return (
    <Sheet open onClose={onClose} title="Land" subtitle={run.workstreamName} placement="auto">
      <LandCard
        branch={run.branch ?? 'branch'}
        target="main"
        title={run.title ?? run.runId}
        {...(run.projectName ? { project: run.projectName } : {})}
        state={cardState}
        steps={landSteps(
          busy && state === 'ready' ? { state: 'running' } : landing,
          landingProgress(view),
        )}
        commits={run.ahead ?? 1}
        files={diff.files}
        additions={diff.additions}
        deletions={diff.deletions}
        tasks={[]}
        {...(landing?.sha ? { landedSha: landing.sha.slice(0, 7) } : {})}
        requiresPasskey
        onLand={async () => {
          setBusy(true)
          try {
            await onLand()
          } finally {
            setBusy(false)
          }
        }}
        onOpen={onClose}
      />
    </Sheet>
  )
}

function summaryDiff(view: TranscriptView) {
  let files = 0
  let additions = 0
  let deletions = 0
  for (const e of view.entries) {
    if (e.kind !== 'turn') continue
    files += e.turn.summary.files
    additions += e.turn.summary.additions
    deletions += e.turn.summary.deletions
  }
  return { files, additions, deletions }
}

/** Words from a failing recipe command that name it in task titles (`e2e:int`, `verify`). */
export function commandKeywords(command: string): string[] {
  const words = command
    .split(/\s+/)
    .filter((w) => w && !w.includes('=') && !w.startsWith('-') && !w.startsWith('@'))
    .filter((w) => !['pnpm', 'npm', 'yarn', 'bun', 'run', 'exec', 'npx'].includes(w))
  const scripts = words.filter((w) => w.includes(':'))
  return (scripts.length ? scripts : words.slice(-1)).map((w) => w.toLowerCase())
}

/** Open tasks that already track a failing command ("Fix e2e:int: …"). */
export function linkedTasks(command: string, tasks: readonly TaskDetail[]): TaskDetail[] {
  const keys = commandKeywords(command)
  if (!keys.length) return []
  return tasks.filter(
    (t) =>
      t.status !== 'done' &&
      t.status !== 'cancelled' &&
      keys.some((k) => t.title.toLowerCase().includes(k)),
  )
}

const RECIPE_STATE: Record<LandingRecipeRun['status'], string> = {
  pending: 'Waiting',
  running: 'Running',
  pass: 'Passed',
  fail: 'Failed',
  reused: 'Already verified',
}

/** Structured failure: phase, exact command, exit code, duration, output tail, next action. */
export function FailureDetail({ landing }: { landing: Landing }) {
  const f = landing.failure
  if (!f) return null
  const text = [
    `Landing ${landing.state}: ${f.phaseLabel}`,
    f.message,
    f.command ? `command: ${f.command}` : '',
    f.exitCode !== undefined ? `exit code: ${f.exitCode}` : '',
    f.durationMs !== undefined ? `duration: ${elapsed(f.durationMs)}` : '',
    f.baselineFailing !== undefined
      ? `fails on target too: ${f.baselineFailing ? 'yes' : 'no'}`
      : '',
    f.files?.length ? `conflicting files: ${f.files.join(', ')}` : '',
    f.logPath ? `full log: ${f.logPath}` : '',
    f.outputTail ? `\n${f.outputTail}` : '',
    `next: ${f.nextAction}`,
  ]
    .filter(Boolean)
    .join('\n')
  return (
    <section
      className="grid gap-1.5 rounded-lg border border-border-subtle p-2"
      data-landing-failure
    >
      <div className="flex items-center gap-2 text-[14px]">
        <span className="font-semibold text-status-fail">{f.phaseLabel}</span>
        <span className="flex-1" />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void navigator.clipboard?.writeText(text).catch(() => {})}
        >
          Copy details
        </Button>
      </div>
      <p className="m-0 text-[14px]">{f.message}</p>
      {f.command ? (
        <p className="m-0 text-[14px] text-text-2">
          <code className="font-mono">{f.command}</code>
          {f.exitCode !== undefined ? ` · exit ${f.exitCode}` : ''}
          {f.durationMs !== undefined ? ` · ${elapsed(f.durationMs)}` : ''}
          {f.baselineFailing !== undefined
            ? f.baselineFailing
              ? ' · also fails on the target'
              : ' · passes on the target'
            : ''}
        </p>
      ) : null}
      {f.files?.length ? (
        <p className="m-0 text-[14px] text-text-2">
          Conflicts:{' '}
          {f.files.map((x) => (
            <code key={x}>{x} </code>
          ))}
        </p>
      ) : null}
      {f.outputTail ? (
        <details open>
          <summary className="cursor-pointer text-[14px] text-text-3">Last output</summary>
          <pre className="m-0 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-surface-2 p-2 text-[14px] leading-4">
            {f.outputTail}
          </pre>
        </details>
      ) : null}
      {f.logPath ? (
        <p className="m-0 text-[14px] text-text-3">
          Full log: <code className="break-all font-mono">{f.logPath}</code>
        </p>
      ) : null}
      <p className="m-0 text-[14px] text-text-2">Next: {f.nextAction}</p>
    </section>
  )
}

/**
 * Landing drill-in (from the status chip): the live recipe log — command, status, exit code,
 * duration, output tail — plus cancel, retry and, for failures main shares, "land anyway".
 */
export function LandingPanel({
  status,
  tasks,
  now,
  onClose,
  onCancel,
  onRetry,
  onLandAnyway,
  onFixTask,
  grant,
  landings,
}: {
  /** landing-modes: this project's landings (stack view). */
  landings?: readonly Landing[]
  /** land-grant: this device's standing grant covers a retry (no passkey tap). */
  grant?: { projectId: string; expiresAt: number }
  status: LandingStatus
  tasks: readonly TaskDetail[]
  now: number
  onClose: () => void
  onCancel: (landingId: string) => void
  onRetry: () => void
  onLandAnyway: () => void
  onFixTask: (command: string) => void
}) {
  const l = status.landing
  const recipe = l?.recipe ?? []
  const failed = recipe.filter((r) => r.status === 'fail')
  const active = status.phase === 'queued' || status.phase === 'verifying'
  const stack = l && landings ? stackView(landings, l) : undefined
  return (
    <Sheet
      open
      onClose={onClose}
      title="Landing"
      subtitle={status.chip?.label}
      placement="auto"
      actions={
        <div className="flex flex-wrap justify-end gap-2">
          {active && l ? (
            <Button variant="secondary" size="sm" onClick={() => onCancel(l.id)}>
              Cancel landing
            </Button>
          ) : null}
          {l?.baselineFailing && status.phase === 'blocked' ? (
            <Button variant="secondary" size="sm" onClick={onLandAnyway}>
              Land anyway (passkey)
            </Button>
          ) : null}
          {status.showLand ? (
            <Button variant="primary" size="sm" onClick={onRetry}>
              {grant ? `${status.landLabel} · within grant` : status.landLabel}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="grid gap-3" data-landing-panel>
        {grant && status.showLand ? (
          <WithinGrantBadge projectId={grant.projectId} expiresAt={grant.expiresAt} />
        ) : null}
        {l ? (
          <p className="m-0 text-[14px] text-text-2">
            {l.auto ? 'Auto-landing' : 'Landing'} <code>{l.branch ?? 'branch'}</code>
            {l.headSha ? (
              <>
                {' '}
                at <code>{l.headSha.slice(0, 7)}</code>
              </>
            ) : null}
            {l.summary ? ` — ${l.summary}` : ''}
          </p>
        ) : (
          <p className="m-0 text-[14px] text-text-2">
            {status.phase === 'auto-pending'
              ? 'Viko lands this branch when the agent’s turn ends and the landing recipe passes. Releases stay separate.'
              : 'Nothing is landing right now.'}
          </p>
        )}
        {l ? (
          <>
            <TargetLine landing={l} />
            <ConflictWait landing={l} />
            <GateEvidence landing={l} />
            {stack ? <StackBlock stack={stack} /> : null}
          </>
        ) : null}
        {recipe.length ? (
          <ol className="m-0 grid list-none gap-2 p-0" aria-label="Landing recipe">
            {recipe.map((r, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: recipe order is stable
                key={i}
                className="grid gap-1 rounded-lg border border-border-subtle p-2"
                aria-label={`${r.command}: ${RECIPE_STATE[r.status]}`}
              >
                <div className="flex min-w-0 items-center gap-2 text-[14px]">
                  <span
                    className={`font-semibold ${
                      r.status === 'pass' || r.status === 'reused'
                        ? 'text-status-ok'
                        : r.status === 'fail'
                          ? 'text-status-fail'
                          : r.status === 'running'
                            ? 'text-status-run'
                            : 'text-text-3'
                    }`}
                  >
                    {RECIPE_STATE[r.status]}
                  </span>
                  <code className="min-w-0 flex-1 truncate font-mono">{r.command}</code>
                  <span className="flex-none text-text-3">
                    {r.exitCode !== undefined ? `exit ${r.exitCode}` : ''}
                    {r.durationMs !== undefined
                      ? ` · ${elapsed(r.durationMs)}`
                      : r.status === 'running' && r.startedAt
                        ? ` · ${elapsed(now - r.startedAt)}`
                        : ''}
                  </span>
                </div>
                {r.status === 'fail' && r.baseline === 'fails' ? (
                  <p className="m-0 text-[14px] text-status-wait">
                    Fails on main too — not caused by this run
                    {r.accepted ? ' (landed anyway)' : ''}.
                  </p>
                ) : null}
                {r.outputTail && (r.status === 'fail' || r.status === 'running') ? (
                  <details open={r.status === 'fail'}>
                    <summary className="cursor-pointer text-[14px] text-text-3">Output</summary>
                    <pre className="m-0 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-surface-2 p-2 text-[14px] leading-4">
                      {r.outputTail}
                    </pre>
                  </details>
                ) : null}
              </li>
            ))}
          </ol>
        ) : null}
        {l?.failure ? <FailureDetail landing={l} /> : null}
        {(status.phase === 'blocked' || status.phase === 'interrupted') &&
        !l?.failure &&
        !recipe.some((r) => r.status === 'fail') ? (
          <p className="m-0 text-[14px] text-status-fail">
            {l?.reason ?? l?.error ?? 'The daemon reported no detail for this landing.'}
          </p>
        ) : null}
        {failed.map((r) => {
          const linked = linkedTasks(r.command, tasks)
          return (
            <div key={r.command} className="grid gap-1 text-[14px]">
              {linked.length ? (
                <p className="m-0 text-text-2">
                  Tracked by:{' '}
                  {linked.map((t, i) => (
                    <span key={t.id}>
                      {i ? ', ' : ''}
                      <Link to="/t/$taskId" params={{ taskId: t.id }} className="text-accent-text">
                        {t.title}
                      </Link>
                    </span>
                  ))}
                </p>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => onFixTask(r.command)}>
                  Open a fix task
                </Button>
              )}
            </div>
          )
        })}
      </div>
    </Sheet>
  )
}
